import React, { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import NexusSLIK from '../../systems/NexusSLIK';
import { isManagerOrOwner } from '../../shared/authority';
import aiSettings from '../../services/AISettingsService';
import aiManager from '../../services/AIManagerService';
import { appointmentService } from '../../services/appointmentService';
import { clientDataService } from '../../services/clientDataService';
import { emergencyBookingService } from '../../services/emergencyBookingService';
import { barberDataService } from '../../services/barberDataService';
import { canBarberTakeEmergency } from '../../shared/emergency';
import { blockedTimesService } from '../../services/blockedTimesService';
import { ymd } from '../../shared/dateUtils';
// SAME PARAMETERS AS THE CUSTOMER-PORTAL EMERGENCY (Jose 2026-08-06): an emergency may squeeze
// AROUND services, but must still respect the barber's schedule (working hours + LUNCH), manual
// Block Times, and the client's per-service emergency protection. These are the shared brains the
// portal/public apps already use, so the POS can't drift from them.
import { getDaySchedule, parseTimeToMinutes, minutesToLabel, bookableSlots, resolveDayHours, type SpecialHours } from '../../shared/availability';
import { mergeBookingRules, BookingRules } from '../../shared/bookingRules';
import { shopSettingsService } from '../../services/shopSettingsService';
import { clientServiceEmergencyAllowed, EMERGENCY_SERVICE } from '../../shared/emergency';
import './EmergencyBookingTile.css';

// Manual Block Times now live in Firestore (blockedTimesService) — the ONE source read by every
// surface. Was localStorage. Service stores date='yyyy-MM-dd' + startTime='HH:mm' (strings) → parse
// LOCAL to a Date so the toDateString() day-match below stays TZ-safe. (Jose 2026-08-12)
interface ManualBlock { barberId: string; start: Date; duration: number; }
function readManualBlocks(): ManualBlock[] {
  try {
    return blockedTimesService.getAll().map(b => ({
      barberId: b.barberId,
      start: new Date(`${b.date}T${b.startTime}`),
      duration: b.duration || 0,
    }));
  } catch { return []; }
}

/**
 * Why is [startMin, startMin+durMin) not placeable for an emergency on this barber/day?
 * Returns { kind, label } or null. `kind` categorizes so the IN-HOUSE owner override can treat
 * after-hours ('hours') differently from a HARD schedule block ('lunch'/'block'):
 *   • 'hours'        — outside the barber's working window (owner may book past close SILENTLY).
 *   • 'lunch'|'block'— barber lunch or a manual Block Time (owner override needs a SPEED-BUMP confirm).
 * Mirrors the calendar's own rules by reading the SAME schedule (getDaySchedule) + Block Times.
 */
function emergencySlotBlockedReason(
  barber: any, date: Date, startMin: number, durMin: number,
): { kind: 'lunch' | 'hours' | 'block'; label: string } | null {
  const endMin = startMin + durMin;
  const sched = getDaySchedule(barber, date);
  if (sched.working) {
    if (sched.breakStart != null && sched.breakEnd != null && startMin < sched.breakEnd && endMin > sched.breakStart) {
      return { kind: 'lunch', label: `${barber?.name || 'the barber'}'s lunch ${minutesToLabel(sched.breakStart)}–${minutesToLabel(sched.breakEnd)}` };
    }
    if (startMin < sched.start || endMin > sched.end) {
      return { kind: 'hours', label: `outside ${barber?.name || 'the barber'}'s working hours` };
    }
  }
  const blocks = readManualBlocks().filter(b => b.barberId === barber?.id && b.start.toDateString() === date.toDateString());
  for (const b of blocks) {
    const bStart = b.start.getHours() * 60 + b.start.getMinutes();
    if (startMin < bStart + b.duration && endMin > bStart) return { kind: 'block', label: 'a blocked time' };
  }
  return null;
}


interface EmergencyBookingTileProps {
  appointments?: any[];
  barbers?: any[];
  services?: any[];
  onBookingComplete?: (booking: any) => void;
  clientData?: {
    id: string;
    name: string;
    phone: string;
    cpi?: number;
    appointmentHistory?: any[];
    preferredBarber?: string; // Add preferred barber to client data
  } | null;
}

interface TimeSlot {
  timeStr: string;         // CANONICAL "HH:MM" — the value booked. Selection + summary key on THIS
                           // (never on the Date below, whose reference changes every render).
  time: Date;              // same instant as timeStr on selectedDate — kept for display helpers
  display: string;         // human label, e.g. "11:15 AM"
  available: boolean;
  overrideReason?: string; // set (owner only) when the slot is on lunch/a block — needs a confirm
  isGap?: boolean;         // true = SQUEEZE-IN (shrinks an existing appointment to fit)
  squeezeAnchor?: string;  // squeeze only: the existing appt's start "HH:MM" that squeezeInEmergency shrinks
  gapMinutes?: number;
}

const EmergencyBookingTile: React.FC<EmergencyBookingTileProps> = ({
  appointments = [],
  barbers = [],
  services = [],
  onBookingComplete,
  clientData = null
}) => {
  console.log('✅ EmergencyBookingTile.tsx LOADED (NEW VERSION WITH DATE PICKER)');
  console.log('  📦 Props received:', { appointments: appointments.length, barbers: barbers.length, services: services.length });

  // EMERGENCY ELIGIBILITY (Jose 2026-08-17): only barbers an admin manually enabled
  // (emergencyBooking.enabled) may take emergencies — the SAME shared rule the customer portal uses,
  // so the two surfaces can never disagree. The `barbers` prop can be thin, so resolve each to its full
  // barber record before gating. Not everybody can do an emergency haircut.
  const eligibleEmergencyBarbers = (barbers || []).filter((b: any) =>
    canBarberTakeEmergency(barberDataService.getBarberById(b.id) || b)
  );

  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [selectedBarber, setSelectedBarber] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date()); // 📅 Add date picker state
  const [urgencyLevel, setUrgencyLevel] = useState<'standard' | 'urgent' | 'critical'>('urgent');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [, setClientCPI] = useState(0); // CPI calculated but not displayed yet
  const [dynamicPricing, setDynamicPricing] = useState<any>(null);
  const [aiSettingsState, setAiSettingsState] = useState(aiSettings.getSettings());
  const [, setBlocksVersion] = useState(0); // bump on Firestore block changes → recompute emergency gates
  // Shop booking parameters — SAME source the calendar + availability brain read, so the emergency
  // finder can't drift from the rest of the app (slot interval, back-to-back, working hours). (Jose 2026-10-03)
  const [businessHoursMap, setBusinessHoursMap] = useState<Record<string, { open: string; close: string; closed: boolean }> | null>(null);
  const [specialHours, setSpecialHours] = useState<SpecialHours | null>(null); // one-off/holiday closures (Jose 2026-11-03)
  const [bookingRules, setBookingRules] = useState<BookingRules | null>(null);
  // Big center-screen "appointment created" confirmation (Jose 2026-10-03) — the corner toast vanished
  // too fast / needed scrolling on a phone; this pops in the middle so it's unmissable, then fades.
  const [successBanner, setSuccessBanner] = useState<{ client: string; barber: string; time: string; total: string } | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const settings = await shopSettingsService.getSettings();
        if (settings?.businessHours) setBusinessHoursMap(settings.businessHours as any);
        setSpecialHours((settings?.specialHours as any) || null);
        setBookingRules(mergeBookingRules(settings?.bookingRules));
      } catch { setBookingRules(mergeBookingRules(undefined)); }
    })();
  }, []);

  // IN-HOUSE OVERRIDE, PER SELECTED BARBER (Jose 2026-08-06): the override (book PAST close +
  // ⚠ speed-bump over a lunch/block) is only sensible for the barber who'd actually be there after
  // hours — the OWNER (or a manager), not a regular barber. Gate on the SELECTED BARBER's role from
  // their barber record (reliable: "Owner"/"Manager"/"Barber"), NOT the login account. Edgar
  // (Barber) → strict to his hours; Jose (Owner)/Kelly (Manager) → after-hours + override. Declared
  // AFTER the state above so `selectedBarber` isn't referenced in its temporal dead zone.
  const canOverride = (() => {
    // Read role from the selected barber (now passed through) OR the canonical record — belt+braces.
    const r = String(
      selectedBarber?.role || barberDataService.getBarberById(selectedBarber?.id)?.role || ''
    ).toLowerCase();
    return isManagerOrOwner(r); // authority brain — owner ⊇ manager
  })();

  // Client search and walk-in states
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [searchedClient, setSearchedClient] = useState<any>(null);
  const [walkInName, setWalkInName] = useState('');
  const [walkInPhone, setWalkInPhone] = useState('');
  const [walkInConfirmed, setWalkInConfirmed] = useState(false);

  const emergencyFee = {
    standard: 25,
    urgent: 37.50,
    critical: 50
  };

  // ✅ Auto-waive the emergency fee when the client's profile says so (Jose 2026-10-21). The waive toggle
  // lives on the client's "Emergency Booking Settings" card (ClientsManagement). It was ONLY honored on the
  // phone-receptionist path — the manual POS tile charged the fee regardless. Check all field spellings
  // (UI writes `waiveFee`; receptionist checks `feeWaived`; legacy `emergencyFeeWaived`).
  const clientWaivesEmergencyFee = (c: any): boolean =>
    !!(c?.emergencyBookingSettings?.waiveFee || c?.emergencyBookingSettings?.feeWaived || c?.emergencyFeeWaived);

  // AI Settings listener
  useEffect(() => {
    const handleSettingsChange = () => {
      setAiSettingsState(aiSettings.getSettings());
    };
    aiSettings.on('settingsChanged', handleSettingsChange);
    return () => {
      aiSettings.off('settingsChanged', handleSettingsChange);
    };
  }, []);

  // Keep the Firestore blocks cache warm so emergency slot-gating (readManualBlocks) sees live blocks
  // even when the calendar isn't mounted. Idempotent; re-render on block changes. (Jose 2026-08-12)
  useEffect(() => {
    blockedTimesService.start();
    const unsub = blockedTimesService.subscribe(() => setBlocksVersion((v) => v + 1));
    return unsub;
  }, []);

  // Emergency booking is now always enabled (AI features are optional enhancements)
  const isEmergencyBookingEnabled = true;

  // Calculate CPI (optional AI feature)
  useEffect(() => {
    const activeClient = searchedClient || clientData;

    if (activeClient && aiSettingsState.enabled && aiSettingsState.modules.clientScoring) {
      // Transform client data to match NexusSLIK ClientData interface
      const cpiData = {
        id: activeClient.id || `client-${Date.now()}`,
        name: activeClient.name || `${activeClient.firstName || ''} ${activeClient.lastName || ''}`.trim() || 'Walk-In',
        visits: activeClient.appointmentHistory?.length || activeClient.totalVisits || 0,
        totalSpent: activeClient.appointmentHistory?.reduce((sum: number, apt: any) => sum + (apt.price || 0), 0) || activeClient.totalSpent || 0,
        lastVisit: activeClient.lastVisit || new Date().toISOString(),
        referrals: 0,
        isModelClient: false,
        membershipType: (activeClient.membershipType || 'regular') as 'regular' | 'premium' | 'vip' | 'model',
        noShowCount: 0,
        cancellationCount: 0
      };

      const cpiResult = NexusSLIK.CPI.calculateCPI(cpiData);
      setClientCPI(cpiResult.score);
    } else if (!activeClient) {
      // Default CPI for walk-ins
      setClientCPI(50);
    }
  }, [clientData, searchedClient, aiSettingsState]);

  // Build the emergency time offers from the SHARED BRAIN (Jose 2026-10-03) — the SAME bookableSlots()
  // the New Appointment picker and customer portal use, so the POS can't drift. Two groups, both keyed
  // by an "HH:MM" string so the highlight / summary / booked time are always the SAME value:
  //   • OPEN    — bookableSlots(): genuinely free, schedule-aware, EXCLUDES real bookings, drops past
  //               times, and (back-to-back) offers the exact moment the barber frees up after an appt.
  //   • SQUEEZE — around an existing appt (>15 min): inject at its start+15 (squeezeInEmergency shrinks
  //               it). Only added when it's a time the OPEN list doesn't already cover.
  // Replaces the old bespoke finder whose "free" branch just listed business hours and never checked
  // real bookings, so it offered times that weren't actually open.
  const buildEmergencySlots = (): TimeSlot[] => {
    if (!selectedBarber) return [];
    const barberForSched = barberDataService.getBarberById(selectedBarber.id) || selectedBarber;
    const availBarber: any = { id: barberForSched.id, availability: barberForSched.availability, name: barberForSched.name };

    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const dateKey = ymd(selectedDate);

    const to24 = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const dateAt = (m: number) => { const d = new Date(selectedDate); d.setHours(Math.floor(m / 60), m % 60, 0, 0); return d; };

    // Appointments from the SERVICE (source of truth, same as CalendarView) — its `.time` is "HH:MM"
    // 24h and `.date` is "yyyy-MM-dd", which both the brain and squeezeInEmergency expect. Fall back to
    // the (converted) prop if the service is somehow empty.
    const rawAppts = ((appointmentService.getAllAppointments?.() as any[]) || appointments || []);
    const dayAppts = rawAppts
      .filter(a => a.barberId === barberForSched.id)
      .filter(a => (a.date === dateKey) || new Date(a.date || a.time).toDateString() === selectedDate.toDateString())
      .filter(a => a.status !== 'cancelled' && a.status !== 'completed')
      .map(a => ({ barberId: barberForSched.id, time: a.time, duration: a.duration || 30, status: a.status || 'confirmed' }));

    // Shop hours for this weekday (barber.availability is the fallback inside getDaySchedule).
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const h = businessHoursMap?.[dayNames[selectedDate.getDay()]];
    const weekly = h ? { closed: h.closed, open: h.open, close: h.close } : undefined;
    const shopHours = resolveDayHours(selectedDate, weekly as any, specialHours) || weekly; // one-off closures WIN (Jose 2026-11-03)
    const rules = bookingRules || mergeBookingRules(undefined);

    const slots: TimeSlot[] = [];
    const seen = new Set<number>();

    // ── OPEN TIMES (the brain) ──
    const openMins = bookableSlots(availBarber, selectedDate, dayAppts, shopHours as any, rules);
    for (const m of openMins) {
      if (isToday && m < nowMin) continue;                        // belt+braces (brain already prunes past)
      const reason = emergencySlotBlockedReason(barberForSched, selectedDate, m, 15);
      if (reason && !canOverride) continue;                       // non-owner: respect lunch/block/hours
      if (seen.has(m)) continue;
      seen.add(m);
      slots.push({
        timeStr: to24(m),
        time: dateAt(m),
        display: minutesToLabel(m),
        available: true,
        isGap: false,
        gapMinutes: 0,
        overrideReason: reason && reason.kind !== 'hours' ? reason.label : undefined,
      });
    }

    // ── SQUEEZE-IN (shrink an existing appt) ──
    // Offer start+15 for each occupying appt long enough to shrink. The WRITE path (squeezeInEmergency)
    // enforces the anchor-client's "block squeezing" protection, so a protected one is refused there.
    const sorted = [...dayAppts].sort((a, b) => (parseTimeToMinutes(a.time) ?? 0) - (parseTimeToMinutes(b.time) ?? 0));
    for (const a of sorted) {
      const aStart = parseTimeToMinutes(a.time);
      if (aStart == null) continue;
      if ((a.duration || 30) <= 15) continue;                     // can't shrink a 15-min appt
      const injMin = aStart + 15;                                 // where squeezeInEmergency lands it
      if (isToday && injMin < nowMin) continue;
      if (seen.has(injMin)) continue;                             // open list already covers this time
      const reason = emergencySlotBlockedReason(barberForSched, selectedDate, injMin, 15);
      if (reason && !canOverride) continue;
      seen.add(injMin);
      slots.push({
        timeStr: to24(injMin),
        time: dateAt(injMin),
        display: minutesToLabel(injMin),
        available: true,
        isGap: true,                                              // "Squeeze" badge
        squeezeAnchor: a.time,                                    // appt start (raw "HH:MM") — shrink THIS
        gapMinutes: 15,
        overrideReason: reason && reason.kind !== 'hours' ? reason.label : undefined,
      });
    }

    // Chronological
    slots.sort((x, y) => (parseTimeToMinutes(x.timeStr) ?? 0) - (parseTimeToMinutes(y.timeStr) ?? 0));
    return slots;
  };

  // Generate emergency time slots and set preferred barber
  useEffect(() => {
    if (showEmergencyModal && selectedBarber) {
      const slots = buildEmergencySlots();
      setAvailableSlots(slots);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmergencyModal, selectedDate, selectedBarber, appointments, businessHoursMap, bookingRules]);

  // EMERGENCY = ONE FIXED SERVICE: the dedicated "Emergency Haircut" ($50) from the shared brain.
  // No menu, no catalog guessing — same service the customer portal uses.
  useEffect(() => {
    if (showEmergencyModal && !selectedService) {
      setSelectedService(EMERGENCY_SERVICE as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmergencyModal]);

  // Auto-select client's preferred barber when modal opens
  useEffect(() => {
    if (showEmergencyModal) {
      const activeClient = searchedClient || clientData;
      if (activeClient?.preferredBarber && barbers.length > 0 && !selectedBarber) {
        const preferredBarberObj = barbers.find(
          b => b.name.toLowerCase().includes(activeClient.preferredBarber!.toLowerCase()) ||
               activeClient.preferredBarber!.toLowerCase().includes(b.name.toLowerCase())
        );
        if (preferredBarberObj) {
          setSelectedBarber(preferredBarberObj);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmergencyModal, clientData, searchedClient, barbers]);

  // Dynamic pricing analysis
  useEffect(() => {
    if (showEmergencyModal && selectedService && aiSettingsState.enabled && aiSettingsState.modules.dynamicPricing) {
      const pricing = aiManager.predictDynamicPricing(
        selectedService.name,
        selectedService.price,
        new Date()
      );
      setDynamicPricing(pricing);
    }
  }, [showEmergencyModal, selectedService, aiSettingsState]);

  const handleEmergencyBooking = async () => {
    console.log('🚨 ===== EMERGENCY BOOKING INITIATED =====');
    console.log('📋 Selected Service:', selectedService);
    console.log('⏰ Selected Time Slot:', selectedTimeSlot);
    console.log('👤 Selected Barber:', selectedBarber);

    // SMART EMERGENCY INJECTION - Shrink existing appointment and squeeze in
    if (selectedService && selectedTimeSlot && selectedBarber) {
      // Determine client information from searched client, clientData prop, or walk-in fields
      const activeClient = searchedClient || clientData;
      const finalClientName = activeClient
        ? (activeClient.name || `${activeClient.firstName || ''} ${activeClient.lastName || ''}`.trim())
        : walkInName;
      const finalClientPhone = activeClient?.phone || walkInPhone;
      const finalClientId = activeClient?.id || `walk-in-${Date.now()}`;

      console.log('👥 Client Details:', {
        activeClient: activeClient ? 'Found' : 'Walk-in',
        finalClientName,
        finalClientPhone,
        finalClientId
      });

      // Use default walk-in info if none provided (emergency bookings should be quick!)
      const bookingClientName = finalClientName || 'Walk-In Client';
      const bookingClientPhone = finalClientPhone || '(000) 000-0000';
      const bookingClientId = finalClientId || `walk-in-${Date.now()}`;

      const activeFeeWaived = clientWaivesEmergencyFee(activeClient);
      const appliedFee = activeFeeWaived ? 0 : emergencyFee[urgencyLevel];
      const basePrice = dynamicPricing?.suggestedPrice || selectedService.price;
      const totalPrice = basePrice + appliedFee;

      console.log('💰 Pricing:', {
        basePrice,
        emergencyFee: appliedFee,
        feeWaived: activeFeeWaived,
        urgencyLevel,
        totalPrice,
        hasDynamicPricing: !!dynamicPricing
      });

      try {
        // targetTime = what squeezeInEmergency receives. For an OPEN slot it's the slot time itself
        // (books directly there). For a SQUEEZE it's the EXISTING appointment's start (squeezeAnchor)
        // so squeezeInEmergency can FIND & shrink it; the emergency then lands at anchor+15 == the
        // slot's displayed time. Either way offered == booked. (Jose 2026-10-03)
        const targetTime = (selectedTimeSlot.isGap && selectedTimeSlot.squeezeAnchor)
          ? selectedTimeSlot.squeezeAnchor
          : selectedTimeSlot.timeStr;
        console.log('⏱️ Target Time (HH:MM):', targetTime, '| lands at:', selectedTimeSlot.timeStr);

        // ── EMERGENCY PARAMETER GATE (Jose 2026-08-06) ─────────────────────────────────
        // An emergency may squeeze AROUND services, but NOT onto a block/lunch/after-hours or a
        // service the client protected — UNLESS the OWNER overrides (in-house tile only). Resolve
        // the CANONICAL barber (same schedule the calendar draws) and enforce here so a bad slot
        // can't be written no matter what the picker offered. Check where the emergency LANDS
        // (timeStr), not the shrink anchor.
        const canonicalBarber = barberDataService.getBarberById(selectedBarber.id) || selectedBarber;
        const startMin = parseTimeToMinutes(selectedTimeSlot.timeStr) ?? 0;
        const EMERGENCY_SLOT_MIN = 15; // the emergency occupies a 15-min slot (see duration below)
        const blocked = emergencySlotBlockedReason(canonicalBarber, selectedDate, startMin, EMERGENCY_SLOT_MIN);
        if (blocked) {
          if (!canOverride) {
            // Non-owner: hard refuse (lunch / block / after-hours all blocked).
            toast.error(`⛔ Can't place an emergency here — ${blocked.label}. Pick another time.`, { duration: 6000 });
            return;
          }
          if (blocked.kind !== 'hours') {
            // OWNER OVERRIDE of a HARD schedule constraint (lunch / manual block) → speed-bump
            // confirm that spells out exactly what's about to happen. 'hours' (after close) is the
            // owner's end-of-day call and needs no confirm.
            const ok = window.confirm(
              `⚠️ OWNER OVERRIDE\n\nYou are about to book an emergency over ${blocked.label} for ${bookingClientName}.\n\nThis bypasses the barber's schedule. Continue?`
            );
            if (!ok) return;
            toast(`⚠️ Owner override — booked over ${blocked.label}.`, { icon: '⚠️', duration: 6000 });
          }
        }
        // Client PROTECT-list stays HARD for EVERYONE (owner included) — it's the client's own
        // consent, not a schedule rule. (If Jose wants owner to override this too, add a stronger
        // confirm here.)
        const clientForGate = (searchedClient || clientData) as any;
        if (!clientServiceEmergencyAllowed(clientForGate, selectedService.name)) {
          toast.error(`⛔ ${bookingClientName} has protected "${selectedService.name}" from emergency squeezing. Not allowed.`, { duration: 6000 });
          return;
        }

        // Use smart injection to shrink and squeeze in
        const result = await appointmentService.squeezeInEmergency(
          targetTime,
          selectedBarber.id,
          {
            clientId: bookingClientId,
            clientName: bookingClientName,
            clientPhone: bookingClientPhone,
            barberId: selectedBarber.id,
            barberName: selectedBarber.name,
            service: selectedService.name,
            price: totalPrice,
            date: ymd(selectedDate), // Use selectedDate instead of slot time
            time: targetTime, // Will be recalculated by squeezeInEmergency
            duration: 15, // Emergency booking duration
            status: 'confirmed' as const,
            notes: `🚨 EMERGENCY BOOKING (${urgencyLevel}) - ${urgencyLevel.toUpperCase()} priority. Emergency fee: $${appliedFee}${activeFeeWaived ? ' (WAIVED — client profile)' : ''}${dynamicPricing ? `. Dynamic pricing: $${dynamicPricing.suggestedPrice}` : ''}`,
            source: 'emergency' as const, // 🚨 Global emergency source (triggers red glow)
            reminderSent: false,
            emergencyBooking: true, // 🚨 Global emergency flag (triggers red glow)
            emergencyFeeWaived: activeFeeWaived, // ✅ waived from the client's profile (record-keeping)
            urgencyLevel: urgencyLevel // Emergency urgency level (standard/urgent/critical)
          },
          {
            minDuration: 15, // Shrink original to 15 min
            emergencyDuration: 15, // Emergency takes 15 min
            allowConflicts: false
          }
        );

        console.log('📦 Smart Injection Result:', result);

        if (result.success) {
          console.log('✅ SUCCESS! Emergency booking created');
          console.log('📅 Original Appointment (Shrunk):', result.originalAppointment);
          console.log('🚨 Emergency Appointment (Injected):', result.emergencyAppointment);

          // 🚨 TRIGGER GLOBAL NOTIFICATION - Creates popup for all staff devices
          try {
            emergencyBookingService.createBooking({
              clientId: bookingClientId,
              clientName: bookingClientName,
              clientPhone: bookingClientPhone,
              clientEmail: '', // Staff booking may not have email
              requestedDate: selectedDate,
              requestedTimeSlot: selectedTimeSlot.display as 'morning' | 'afternoon' | 'evening' | 'asap',
              serviceType: selectedService.name,
              urgencyLevel: urgencyLevel === 'standard' ? 'high' : 'critical',
              notes: `Staff emergency booking - ${selectedBarber.name} at ${selectedTimeSlot.display}`
            });
            console.log('🔔 Emergency notification created for all staff devices');
          } catch (notifError) {
            console.warn('Could not create notification:', notifError);
          }

          toast.success(`✅ ${result.message}\nTotal: $${totalPrice.toFixed(2)}`, {
            duration: 5000
          });

          // BIG center-screen confirmation — captured now (state is reset 300ms later). Shows the
          // ACTUAL booked time from the write result (falls back to the offered time, which now matches).
          const landedTime = result.emergencyAppointment?.time
            ? minutesToLabel(parseTimeToMinutes(result.emergencyAppointment.time) ?? 0)
            : selectedTimeSlot.display;
          setSuccessBanner({
            client: bookingClientName,
            barber: selectedBarber.name,
            time: landedTime,
            total: totalPrice.toFixed(2),
          });
          setTimeout(() => setSuccessBanner(null), 4200);

          // Show warnings if any
          if (result.warnings && result.warnings.length > 0) {
            console.warn('⚠️ Warnings:', result.warnings);
            setTimeout(() => {
              toast(`⚠️ Warning: ${result.warnings!.join(', ')}`, {
                duration: 4000,
                icon: '⚠️'
              });
            }, 1000);
          }

          // Call the booking complete handler
          if (onBookingComplete) {
            console.log('📞 Calling onBookingComplete callback');
            onBookingComplete({
              ...result.emergencyAppointment,
              timeDisplay: result.emergencyAppointment?.time || selectedTimeSlot.display,
              type: 'emergency',
              fee: appliedFee,
              total: totalPrice,
              shrunkAppointment: result.originalAppointment
            });
          }

          console.log('🔄 Closing modal and resetting state...');
          // Close modal immediately
          setShowEmergencyModal(false);

          // Reset after a small delay
          setTimeout(() => {
            setSelectedService(null);
            setSelectedTimeSlot(null);
            setSelectedBarber(null);
            setSelectedDate(new Date()); // Reset to today
            setUrgencyLevel('urgent');
            setClientSearchQuery('');
            setSearchedClient(null);
            setWalkInName('');
            setWalkInPhone('');
            setWalkInConfirmed(false); // Reset confirmation flag
            console.log('✨ Emergency booking flow complete - state reset');
          }, 300);
        } else {
          console.error('❌ FAILED:', result.message);
          // Show error from squeezeInEmergency
          toast.error(`❌ ${result.message}`, {
            duration: 5000
          });
        }
      } catch (error) {
        console.error('💥 EXCEPTION in emergency booking:', error);
        console.error('Stack trace:', error);
        toast.error('❌ Failed to create emergency booking. Please try again.');
      }
    }
  };

  // Don't render if feature is disabled
  if (!isEmergencyBookingEnabled) {
    return null;
  }

  // CPI is optional - no longer blocks emergency booking
  // Emergency booking is available to all clients (matches customer portal behavior)

  return (
    <>
      {/* BIG center-screen success confirmation (Jose 2026-10-03). Fixed + high z-index so it's seen
          regardless of scroll position; auto-dismisses after ~4s and taps away. */}
      {successBanner && (
        <div
          onClick={() => setSuccessBanner(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
            padding: '20px', cursor: 'pointer', animation: 'slikFadeIn 0.2s ease-out',
          }}
        >
          <style>{`
            @keyframes slikPop { 0%{transform:scale(0.7);opacity:0} 60%{transform:scale(1.04)} 100%{transform:scale(1);opacity:1} }
            @keyframes slikFadeIn { from{opacity:0} to{opacity:1} }
          `}</style>
          <div style={{
            animation: 'slikPop 0.4s cubic-bezier(0.18,0.89,0.32,1.28)',
            background: 'linear-gradient(160deg,#16a34a,#15803d)', color: 'white',
            borderRadius: '24px', padding: '30px 26px', maxWidth: '400px', width: '100%',
            textAlign: 'center', boxShadow: '0 25px 60px rgba(0,0,0,0.45), 0 0 0 4px rgba(255,255,255,0.12)',
          }}>
            <div style={{ fontSize: '60px', lineHeight: 1, marginBottom: '6px' }}>🎉</div>
            <div style={{ fontSize: '30px', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: '4px' }}>Boom! Booked.</div>
            <div style={{ fontSize: '15px', fontWeight: 600, opacity: 0.95, marginBottom: '18px' }}>Emergency appointment created</div>
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '14px', padding: '14px 16px', textAlign: 'left', fontSize: '15px', lineHeight: 1.9 }}>
              <div><span style={{ opacity: 0.85 }}>Client</span><span style={{ float: 'right', fontWeight: 700 }}>{successBanner.client}</span></div>
              <div><span style={{ opacity: 0.85 }}>Barber</span><span style={{ float: 'right', fontWeight: 700 }}>{successBanner.barber}</span></div>
              <div><span style={{ opacity: 0.85 }}>Time</span><span style={{ float: 'right', fontWeight: 700 }}>{successBanner.time}</span></div>
              <div><span style={{ opacity: 0.85 }}>Total</span><span style={{ float: 'right', fontWeight: 700 }}>${successBanner.total}</span></div>
            </div>
            <div style={{ marginTop: '16px', fontSize: '13px', opacity: 0.85 }}>Tap anywhere to dismiss</div>
          </div>
        </div>
      )}

      <div
        className="emergency-booking-tile"
        onClick={() => {
          console.log('🚨 Emergency tile clicked - Opening modal');
          setUrgencyLevel('urgent'); // Default to urgent
          setShowEmergencyModal(true);
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="tile-header">
          <span className="tile-icon">🚨</span>
          <h4>Emergency Booking</h4>
          <span className="emergency-badge">Active</span>
        </div>
        <div className="tile-content">
          <div className="emergency-info">
            <p>Fully booked? No problem!</p>
            <p className="fee-notice">
              Tap to squeeze in a client • Fee: $25-$50
            </p>
          </div>
        </div>
      </div>

      {showEmergencyModal && (
        <div className="emergency-modal" style={{ zIndex: 9999 }}>
          <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3>🚨 Emergency Booking</h3>
            <button
              className="close-btn"
              onClick={() => setShowEmergencyModal(false)}
            >
              ×
            </button>

            {/* Client Search Section - LIVE AUTOCOMPLETE */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <h4 style={{ marginBottom: '10px', fontWeight: 600, fontSize: '14px', color: '#374151' }}>
                👤 Client Info
              </h4>

              {/* Single Search/Entry Field */}
              {!searchedClient && !clientData && !walkInConfirmed ? (
                <>
                  <input
                    type="text"
                    placeholder="🔍 Start typing name or phone..."
                    value={clientSearchQuery}
                    onChange={(e) => setClientSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '15px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    autoFocus
                  />

                  {/* Live Search Results Dropdown */}
                  {clientSearchQuery.length >= 2 && (
                    <div style={{
                      position: 'absolute',
                      zIndex: 10000,
                      width: 'calc(100% - 48px)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: 'white',
                      border: '2px solid #3b82f6',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      marginTop: '-8px'
                    }}>
                      {(() => {
                        const results = clientDataService.searchClients(clientSearchQuery);
                        if (results.length === 0) {
                          return (
                            <div style={{ padding: '16px' }}>
                              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
                                No existing client found
                              </div>
                              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>
                                New walk-in customer:
                              </div>
                              <input
                                type="text"
                                placeholder="Full Name"
                                value={walkInName}
                                onChange={(e) => setWalkInName(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  marginBottom: '8px'
                                }}
                              />
                              <input
                                type="tel"
                                placeholder="Phone Number"
                                value={walkInPhone}
                                onChange={(e) => setWalkInPhone(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  marginBottom: '12px'
                                }}
                              />
                              <button
                                onClick={() => {
                                  if (walkInName && walkInPhone) {
                                    setWalkInConfirmed(true); // Confirm walk-in client
                                    setClientSearchQuery(''); // Close dropdown
                                  } else {
                                    toast.error('Please enter both name and phone number');
                                  }
                                }}
                                disabled={!walkInName || !walkInPhone}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  backgroundColor: walkInName && walkInPhone ? '#22c55e' : '#d1d5db',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  cursor: walkInName && walkInPhone ? 'pointer' : 'not-allowed',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  if (walkInName && walkInPhone) {
                                    e.currentTarget.style.backgroundColor = '#16a34a';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (walkInName && walkInPhone) {
                                    e.currentTarget.style.backgroundColor = '#22c55e';
                                  }
                                }}
                              >
                                ✓ Add Walk-In Client
                              </button>
                            </div>
                          );
                        }
                        return results.slice(0, 5).map((client) => (
                          <button
                            key={client.id}
                            onClick={() => {
                              setSearchedClient(client);
                              setClientSearchQuery('');
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              textAlign: 'left',
                              border: 'none',
                              borderBottom: '1px solid #e5e7eb',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                {client.firstName} {client.lastName}
                                {client.membershipType === 'vip' && (
                                  <span style={{
                                    marginLeft: '8px',
                                    padding: '2px 6px',
                                    backgroundColor: '#fef3c7',
                                    color: '#92400e',
                                    borderRadius: '4px',
                                    fontSize: '11px'
                                  }}>VIP</span>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                {client.phone}
                              </div>
                            </div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {client.totalVisits || 0} visits
                            </div>
                          </button>
                        ));
                      })()}
                    </div>
                  )}
                </>
              ) : (
                /* Selected Client Display - Compact */
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: '#dcfce7',
                  border: '2px solid #22c55e',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#166534', fontSize: '14px' }}>
                      ✓ {(searchedClient || clientData)?.firstName || walkInName} {(searchedClient || clientData)?.lastName || ''}
                    </div>
                    <div style={{ fontSize: '12px', color: '#15803d', marginTop: '2px' }}>
                      {(searchedClient || clientData)?.phone || walkInPhone}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSearchedClient(null);
                      setClientSearchQuery('');
                      setWalkInName('');
                      setWalkInPhone('');
                      setWalkInConfirmed(false); // Reset confirmation
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'white',
                      border: '1px solid #22c55e',
                      borderRadius: '6px',
                      color: '#166534',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600
                    }}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>

            {/* 📅 DATE - Simplified for Same-Day (99% use case) */}
            <div style={{ marginBottom: '16px', padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>📅 Date</div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
                  {selectedDate.toDateString() === new Date().toDateString()
                    ? 'Today'
                    : selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Hidden native date input for reliable date picking */}
                <input
                  type="date"
                  value={`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`}
                  min={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`}
                  max={(() => { const d = new Date(); d.setDate(d.getDate() + 30); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Parse YYYY-MM-DD as local date (not UTC)
                      const [year, month, day] = e.target.value.split('-').map(Number);
                      const newDate = new Date(year, month - 1, day);
                      console.log('📅 Emergency date changed to:', newDate.toDateString());
                      setSelectedDate(newDate);
                      setSelectedTimeSlot(null); // Reset time slot for new date
                      setAvailableSlots([]); // Clear slots — useEffect will recalculate
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#374151',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    maxWidth: '140px'
                  }}
                />
                {selectedDate.toDateString() !== new Date().toDateString() && (
                  <button
                    onClick={() => {
                      setSelectedDate(new Date());
                      setSelectedTimeSlot(null);
                      setAvailableSlots([]);
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#eff6ff',
                      border: '1px solid #93c5fd',
                      borderRadius: '6px',
                      color: '#2563eb',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600
                    }}
                  >
                    Today
                  </button>
                )}
              </div>
            </div>

            {/* Dynamic Pricing Banner */}
            {dynamicPricing && dynamicPricing.suggestedPrice !== selectedService?.price && (
              <div style={{
                padding: '12px',
                background: 'linear-gradient(to right, #eef2ff, #f3e8ff)',
                border: '2px solid #c7d2fe',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <TrendingUp className="h-5 w-5" style={{ color: '#4f46e5', flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <h4 style={{ fontWeight: 600, color: '#3730a3', margin: 0 }}>AI Dynamic Pricing</h4>
                      <span style={{ fontSize: '20px', fontWeight: 700, color: '#4f46e5' }}>
                        ${dynamicPricing.suggestedPrice.toFixed(2)}
                      </span>
                    </div>
                    <p style={{ fontSize: '14px', color: '#4338ca', margin: '0 0 8px 0' }}>
                      {dynamicPricing.reasoning}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#e0e7ff',
                        color: '#3730a3',
                        borderRadius: '9999px',
                        fontSize: '12px'
                      }}>
                        {dynamicPricing.factors.demandLevel}
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#e0e7ff',
                        color: '#3730a3',
                        borderRadius: '9999px',
                        fontSize: '12px'
                      }}>
                        {dynamicPricing.factors.timeOfDay}
                      </span>
                      {dynamicPricing.factors.isPeakDay && (
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: '#e0e7ff',
                          color: '#3730a3',
                          borderRadius: '9999px',
                          fontSize: '12px'
                        }}>
                          Peak Day
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Barber Selection - Photos with initials fallback like main booking */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <h4 style={{ marginBottom: '10px', fontWeight: 600, fontSize: '14px', color: '#374151' }}>
                💈 Select Barber
              </h4>

              {eligibleEmergencyBarbers.length === 0 && (
                <div style={{ padding: '12px', borderRadius: '8px', background: '#fef3c7', color: '#92400e', fontSize: '13px', lineHeight: 1.4 }}>
                  No barbers are enabled for emergency bookings. An admin can turn it on from a barber's profile (Emergency Bookings toggle).
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {eligibleEmergencyBarbers.map((barber: any, idx: number) => {
                  const isSelected = selectedBarber?.id === barber.id;
                  const isPreferred = clientData?.preferredBarber && (
                    barber.name.toLowerCase().includes(clientData.preferredBarber.toLowerCase()) ||
                    clientData.preferredBarber.toLowerCase().includes(barber.name.toLowerCase())
                  );

                  // Check if barber has a photo URL from Firebase
                  const hasPhoto = barber.avatar?.startsWith('http');
                  // Generate initials from name
                  const initials = barber.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                  // Gradient colors for initials
                  const colors = [
                    'linear-gradient(135deg, #8b5cf6, #6366f1)',
                    'linear-gradient(135deg, #3b82f6, #06b6d4)',
                    'linear-gradient(135deg, #22c55e, #10b981)',
                    'linear-gradient(135deg, #f97316, #ef4444)',
                    'linear-gradient(135deg, #ec4899, #f43f5e)',
                  ];
                  const color = colors[idx % colors.length];

                  return (
                    <button
                      key={barber.id}
                      onClick={() => setSelectedBarber(barber)}
                      style={{
                        padding: '12px',
                        border: isSelected ? '3px solid #8b5cf6' : '2px solid #e5e7eb',
                        backgroundColor: isSelected ? '#faf5ff' : 'white',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        position: 'relative',
                        transition: 'all 0.2s',
                        boxShadow: isSelected ? '0 4px 6px rgba(139, 92, 246, 0.2)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#c4b5fd';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {/* Circular Photo or Initials */}
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: isSelected ? '3px solid #8b5cf6' : '3px solid #e5e7eb',
                          background: hasPhoto ? '#f3f4f6' : color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {hasPhoto ? (
                            <img
                              src={barber.avatar}
                              alt={barber.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                // Fallback to initials if image fails
                                e.currentTarget.style.display = 'none';
                                const parent = e.currentTarget.parentElement;
                                if (parent) {
                                  parent.style.background = color;
                                  parent.innerHTML = `<span style="font-size: 20px; font-weight: bold; color: white">${initials}</span>`;
                                }
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: '20px', fontWeight: 'bold', color: 'white' }}>{initials}</span>
                          )}
                        </div>

                        {/* Available Badge */}
                        <div style={{
                          position: 'absolute',
                          bottom: '-4px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          padding: '2px 8px',
                          backgroundColor: '#22c55e',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 600,
                          borderRadius: '10px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          whiteSpace: 'nowrap'
                        }}>
                          Available
                        </div>

                        {/* Preferred Star */}
                        {isPreferred && (
                          <div style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            fontSize: '16px',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                          }}>⭐</div>
                        )}
                      </div>

                      {/* Barber Name */}
                      <span style={{
                        fontWeight: 600,
                        fontSize: '13px',
                        textAlign: 'center',
                        color: isSelected ? '#7c3aed' : '#374151'
                      }}>
                        {barber.name}
                      </span>

                      {/* Selected Checkmark */}
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#8b5cf6',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Service Selection - Emergency-eligible services only - Grouped by Category */}
            {/* EMERGENCY = ONE SERVICE, A HAIRCUT (Jose 2026-08-06). No service menu — the standard
                haircut is auto-selected (resolveEmergencyHaircut). Shown locked so the operator just
                picks a barber + time. */}
            <div className="service-selection">
              <h4>Emergency Service</h4>
              {selectedService ? (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', borderRadius: '10px', border: '2px solid #8b5cf6',
                  backgroundColor: '#faf5ff',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>💈</span>
                    <div>
                      <div style={{ fontWeight: 700, color: '#111827' }}>{selectedService.name}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Quick haircut · {selectedService.duration || 15} min</div>
                    </div>
                  </div>
                  <span style={{ fontWeight: 700, color: '#7c3aed' }}>${selectedService.price}</span>
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '12px' }}>
                  No haircut service found. Add a haircut service in Settings.
                </p>
              )}
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                Emergency bookings are a single quick haircut.
              </p>
            </div>

            {/* Time Slot Selection */}
            <div className="time-slot-selection">
              <h4>Select Emergency Time Slot:</h4>
              <div className="time-slots-grid">
                {availableSlots.map((slot, index) => (
                  <button
                    key={index}
                    className={`time-slot ${selectedTimeSlot?.timeStr === slot.timeStr ? 'selected' : ''} ${!slot.available ? 'unavailable' : ''} ${slot.isGap ? 'gap-slot' : ''}`}
                    onClick={() => slot.available && setSelectedTimeSlot(slot)}
                    disabled={!slot.available}
                    title={slot.overrideReason ? `Owner override — this is ${slot.overrideReason}` : undefined}
                    style={{
                      position: 'relative',
                      border: slot.overrideReason ? '2px solid #f59e0b' : slot.isGap ? '2px solid #22c55e' : undefined,
                      backgroundColor: slot.overrideReason ? '#fffbeb' : slot.isGap ? '#f0fdf4' : undefined
                    }}
                  >
                    {slot.display}
                    {slot.overrideReason && (
                      <span style={{ position: 'absolute', top: '4px', right: '4px', fontSize: '10px' }} title={slot.overrideReason}>⚠️</span>
                    )}
                    {slot.isGap && (
                      <span style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        fontSize: '10px',
                        padding: '2px 4px',
                        backgroundColor: '#22c55e',
                        color: 'white',
                        borderRadius: '4px'
                      }}>
                        Squeeze
                      </span>
                    )}
                    {!slot.available && <span className="booked">Booked</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Urgency Selection */}
            <div className="urgency-selection">
              <h4>Urgency Level:</h4>
              <select
                value={urgencyLevel}
                onChange={(e) => setUrgencyLevel(e.target.value as 'standard' | 'urgent' | 'critical')}
              >
                <option value="standard">Standard (+$25)</option>
                <option value="urgent">Urgent (+$37.50)</option>
                <option value="critical">Critical (+$50)</option>
              </select>
            </div>

            {/* Booking Summary */}
            {selectedService && selectedTimeSlot && selectedBarber && (
              <div className="booking-summary">
                <div className="summary-line">
                  <span>Service:</span>
                  <span>${dynamicPricing?.suggestedPrice?.toFixed(2) || selectedService.price}</span>
                </div>
                <div className="summary-line">
                  <span>Barber:</span>
                  <span>{selectedBarber.name}</span>
                </div>
                <div className="summary-line">
                  <span>Time:</span>
                  <span>{selectedTimeSlot.display}{selectedTimeSlot.isGap && ' (Squeeze)'}</span>
                </div>
                <div className="summary-line emergency-fee">
                  <span>Emergency Fee:</span>
                  <span>{clientWaivesEmergencyFee(searchedClient || clientData)
                    ? <span style={{ color: '#16a34a', fontWeight: 700 }}>Waived ✓</span>
                    : `$${emergencyFee[urgencyLevel]}`}</span>
                </div>
                <div className="summary-line total">
                  <span>Total:</span>
                  <span>${((dynamicPricing?.suggestedPrice || selectedService.price) + (clientWaivesEmergencyFee(searchedClient || clientData) ? 0 : emergencyFee[urgencyLevel])).toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Missing selections warning */}
            {(!selectedBarber || !selectedService || !selectedTimeSlot) && (
              <div style={{
                padding: '10px',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                marginBottom: '12px',
                fontSize: '13px',
                color: '#92400e'
              }}>
                <strong>Please select:</strong>
                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  {!selectedBarber && <li>A barber</li>}
                  {!selectedService && <li>A service</li>}
                  {!selectedTimeSlot && <li>A time slot {!selectedBarber && '(select barber first)'}</li>}
                </ul>
              </div>
            )}

            <button
              className="confirm-btn"
              onClick={handleEmergencyBooking}
              disabled={!selectedService || !selectedTimeSlot || !selectedBarber}
              style={{
                opacity: (!selectedService || !selectedTimeSlot || !selectedBarber) ? 0.5 : 1,
                cursor: (!selectedService || !selectedTimeSlot || !selectedBarber) ? 'not-allowed' : 'pointer'
              }}
            >
              {(!selectedService || !selectedTimeSlot || !selectedBarber)
                ? 'Complete selections above'
                : 'Confirm Emergency Booking'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EmergencyBookingTile;
